import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const GallerySection = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await fetch('./admin/api/gallery.php');
      const data = await res.json();
      if (data.success) setGallery(data.data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  const openLightbox = (item, idx) => {
    setSelectedImage(item);
    setSelectedIdx(idx);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = '';
  }, []);

  const navigate = useCallback((dir) => {
    const newIdx = (selectedIdx + dir + gallery.length) % gallery.length;
    setSelectedIdx(newIdx);
    setSelectedImage(gallery[newIdx]);
  }, [selectedIdx, gallery]);

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedImage, navigate, closeLightbox]);

  const shown = gallery.slice(0, 12);

  return (
    <section ref={ref} className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="section-badge">
            <i className="fas fa-images" />
            Photo Gallery
          </div>
          <h2 className="section-title">Capturing Our Precious Moments</h2>
          <p className="section-subtitle mx-auto">A glimpse into the vibrant life at PAK School &amp; College System.</p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center py-20 gap-4">
            <div className="spinner" />
            <p className="text-gray-400 text-sm font-inter">Loading gallery...</p>
          </div>
        )}

        {/* Empty */}
        {!loading && gallery.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
              <i className="fas fa-camera text-3xl text-gray-300" />
            </div>
            <h4 className="font-poppins font-700 text-gray-700 text-lg mb-2">No Photos Yet</h4>
            <p className="text-gray-400 text-sm font-inter">We're capturing amazing moments to share soon!</p>
          </div>
        )}

        {/* Masonry-style grid */}
        {!loading && gallery.length > 0 && (
          <>
            <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {shown.map((item, idx) => (
                <div
                  key={item.id}
                  className={`group relative overflow-hidden bg-gray-100 rounded-xl sm:rounded-2xl cursor-pointer
                    ${idx === 0 || idx === 7 ? 'sm:col-span-2 sm:row-span-2' : ''}
                    transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5`}
                  onClick={() => openLightbox(item, idx)}
                  style={{ aspectRatio: idx === 0 || idx === 7 ? '1/1' : '1/1' }}
                  role="button"
                  aria-label={`View photo: ${item.title}`}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && openLightbox(item, idx)}
                >
                  <img
                    src={`admin/${item.image_path}`}
                    alt={item.title || 'School photo'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    style={{ aspectRatio: '1/1' }}
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-all duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-lg">
                      <i className="fas fa-expand text-navy text-sm" />
                    </div>
                  </div>
                  {/* Title on hover */}
                  {item.title && (
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-navy/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-xs font-inter truncate">{item.title}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* View all CTA */}
            <div className={`text-center mt-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <Link to="/gallery" className="btn-primary inline-flex">
                <i className="fas fa-images" />
                View Full Gallery
                <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Photo lightbox"
        >
          {/* Close */}
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <i className="fas fa-times" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 text-white/60 text-sm font-inter">
            {selectedIdx + 1} / {gallery.length}
          </div>

          {/* Prev */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            aria-label="Previous photo"
          >
            <i className="fas fa-chevron-left" />
          </button>

          {/* Next */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
            aria-label="Next photo"
          >
            <i className="fas fa-chevron-right" />
          </button>

          {/* Image */}
          <div
            className="relative max-w-4xl max-h-[80vh] w-full mx-auto"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              if (Math.abs(dx) > 50) navigate(dx < 0 ? 1 : -1);
            }}
          >
            <img
              src={`admin/${selectedImage.image_path}`}
              alt={selectedImage.title || 'School photo'}
              className="w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
            />
            {selectedImage.title && (
              <div className="absolute -bottom-8 left-0 right-0 text-center text-white/60 text-sm font-inter">
                {selectedImage.title}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
